/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import track from 'react-tracking';
import { Helmet } from 'react-helmet-async';
import { injectIntl } from 'gatsby-plugin-react-intl';
import getOptimizelyProps from '@utils/thirdParty';
import SpeedCurveSnippet from '@prospectus/common/ui/SpeedCurveSnippet';
import { SPEEDCURVE_LABEL_EDX_COURSE_ABOUT } from '@utils/speedCurve';
import { intlShape } from '@prospectus/common/types/intlShape';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import setActiveCourseRun from '@data/actions/activeCourseRun';
import setCurrentPage from '@data/actions/currentPage';
import courseShape from '@prospectus/common/types/courseShape';
import { allCurrencyShape } from '@prospectus/common/types/allCurrencyShape';
import useBreakpointMatch from '@prospectus/common/hooks/useBreakpointMatch';

import Layout from '@prospectus/common/ui/Layout';
import SEOPage from '@prospectus/seo/ui/SEOPage';
import SEOCourse from '@prospectus/seo/ui/SEOCourse';
import CoursePage from '@prospectus/open-course-page/components/CoursePage';
import CoursePageMobile from '@prospectus/open-course-page/components/CoursePageMobile';

import { emitEventIfAble } from '@utils/analytics';
import { PRODUCT_MAP_EDX } from '@utils/discoveryConstants';
import { ShowAuditModeExpirationDateContext, WebviewContext } from '@utils/context';
import { getOverriddenFields, seoContentTypeShape, customMetaTagShape } from '@utils/seo';
import {
  getCoursePaymentOption,
  isArchived,
  sharedMessages,
  getCourseImageUrl,
  processCourse,
  getCurrentRun,
  UPGRADABLE,
} from '@utils/course';

import { getDefaultActiveProgram, sortProgramsByPreferredType } from '@utils/program';

import { withLocation } from '@utils/navigation';
import { getUserLoggedIn } from '@data/actions/user';
import { enterpriseUser } from '@data/actions/enterprise';

function OpenCourseDetailPage({
  location,
  pageContext: {
    course,
    allCurrency,
    webview,
    customMetaTags,
    customSeoFields,
    fbeExemptCoursesMap,
    showAuditModeExpirationDate,
  },
  onSetActiveCourseRun,
  search,
  activeCourseRun,
  intl,
  userLoggedIn,
  isEnterpriseUser,
  requestUserLoggedIn,
  requestIsEnterpriseUser,
}) {
  const isMobile = useBreakpointMatch('small');

  // programs
  const sortedPrograms = sortProgramsByPreferredType(course.programs);

  // course runs
  const currentRun = getCurrentRun(search, course);
  const runIsArchived = isArchived(currentRun);
  onSetActiveCourseRun(currentRun);

  const [activeProgram, setActiveProgram] = useState(getDefaultActiveProgram(sortedPrograms));
  const [defaultActiveCourseRun] = useState(currentRun);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [savedCourse] = useState(location.search.includes('save_for_later=true'));

  const courseRun = activeCourseRun.key ? activeCourseRun : defaultActiveCourseRun;
  processCourse(course, courseRun, intl);

  // set up refs for page scroll
  const aboutRef = useRef();
  const learnRef = useRef();
  const syllabusRef = useRef();
  const instructorsRef = useRef();
  const faqsRef = useRef();
  const waysRef = useRef();
  const enrollRef = useRef();

  // create refs to scroll on course pages
  const refMap = {
    aboutRef,
    learnRef,
    syllabusRef,
    instructorsRef,
    faqsRef,
    waysRef,
    enrollRef,
  };

  // create semantic map for refs
  const semanticRefMap = {
    'nav-about': aboutRef,
    'nav-outcome': learnRef,
    'nav-syllabus': syllabusRef,
    'nav-instructors': instructorsRef,
    'nav-faqs': faqsRef,
    'nav-modes': waysRef,
    'mobile-nav-about': aboutRef,
    'mobile-nav-outcome': learnRef,
    'mobile-nav-syllabus': syllabusRef,
    'mobile-nav-instructors': instructorsRef,
    'mobile-nav-faqs': faqsRef,
    'mobile-nav-modes': waysRef,
    'nav-enroll': enrollRef,
  };

  const eventProperties = {
    course_id: currentRun.key,
    is_enrollable: currentRun.isEnrollable,
    is_archived: runIsArchived,
    is_primary_course: currentRun.uuid === course.activeCourseRun.uuid,
    nonInteraction: 1,
    label: currentRun.key,
    redesign: true,
  };

  emitEventIfAble('edx.bi.course-about-page.viewed', eventProperties);

  const scrollToRef = (event) => {
    const ref = semanticRefMap[event.target.id];
    if (ref && ref.current) {
      ref.current.scrollIntoView();
      ref.current.focus({ preventScroll: false });
    }
  };

  useEffect(() => {
    if (savedCourse) {
      window.analytics.track('edx.bi.user.saveforlater.course.viewed');
    }
    requestUserLoggedIn();
    requestIsEnterpriseUser();
  }, []);
  const formattedTitle = `${course.owners[0].key}: ${course.title}`;

  const getCoursePageSEO = (overrides) => ({
    title: overrides.title || formattedTitle,
    description: overrides.description || course.strippedShortDescription || course.shortDescription,
    image: getCourseImageUrl(course),
    pathname: overrides.pathname || location.pathname,
    metaTags: overrides.metaTags || [],
  });

  const overrides = getOverriddenFields(intl, customSeoFields, customMetaTags);

  const isCourseExcluded = course.uuid in fbeExemptCoursesMap;

  const paymentOption = getCoursePaymentOption(course);
  const showCourseModes = !isCourseExcluded && paymentOption === UPGRADABLE;

  const courseProps = {
    scrollToRef,
    refMap,
    semanticRefMap,
    courseRun,
    course,
    activeProgram,
    setActiveProgram,
    activeCourseRun,
    showCourseModes,
    emailOptIn,
    setEmailOptIn,
    sortedPrograms,
    sharedMessages,
    paymentOption,
    webview,
    userLoggedIn,
    isEnterpriseUser,
  };

  const layoutProps = {
    title: overrides.title || formattedTitle,
    mainClassname: 'container_disabled',
    pageClassname: 'course-info-page',
    allCurrency,
    webview,
    pageType: PRODUCT_MAP_EDX.COURSE.NAME,
  };

  return (
    <Layout {...layoutProps}>
      {!isMobile ? (
        <Helmet>
          <script {...getOptimizelyProps({ sync: true, oneTrustIgnore: true })} />
        </Helmet>
      ) : null}
      <SpeedCurveSnippet pageLabel={SPEEDCURVE_LABEL_EDX_COURSE_ABOUT} />
      <WebviewContext.Provider value={webview}>
        <ShowAuditModeExpirationDateContext.Provider value={showAuditModeExpirationDate}>
          {isMobile ? <CoursePageMobile {...courseProps} /> : <CoursePage {...courseProps} />}
        </ShowAuditModeExpirationDateContext.Provider>
        <SEOPage meta={getCoursePageSEO(overrides)} />
        <SEOCourse
          course={course}
          activeCourseRun={courseRun}
          imageUrl={getCourseImageUrl(course)}
          intl={intl}
          hasCertificate={showCourseModes}
        />
      </WebviewContext.Provider>
    </Layout>
  );
}

OpenCourseDetailPage.propTypes = {
  pageContext: PropTypes.shape({
    allMarkdownRemark: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string,
            frontmatter: PropTypes.shape({
              type: PropTypes.string,
              slug: PropTypes.string,
              openGraphImage: PropTypes.string,
              numberOfCourses: PropTypes.number,
            }),
          }),
        }),
      ),
    }),
    allCurrency: allCurrencyShape.isRequired,
    course: PropTypes.shape(courseShape),
    webview: PropTypes.bool,
    customSeoFields: PropTypes.arrayOf(seoContentTypeShape),
    customMetaTags: PropTypes.arrayOf(customMetaTagShape),
    sampleCertificateImage: PropTypes.string,
    fbeExemptCoursesMap: PropTypes.shape(),
    showAuditModeExpirationDate: PropTypes.bool,
  }).isRequired,
  location: PropTypes.shape({
    host: PropTypes.string,
    protocol: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  onSetActiveCourseRun: PropTypes.func,
  search: PropTypes.shape({
    run: PropTypes.string,
  }),
  intl: intlShape.isRequired,
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  tracking: PropTypes.shape().isRequired,
  requestUserLoggedIn: PropTypes.func,
  userLoggedIn: PropTypes.bool,
  isEnterpriseUser: PropTypes.bool,
  requestIsEnterpriseUser: PropTypes.func,
  setPage: PropTypes.func,
};

OpenCourseDetailPage.defaultProps = {
  onSetActiveCourseRun: () => {},
  search: {},
  requestUserLoggedIn: () => {},
  userLoggedIn: false,
  isEnterpriseUser: false,
  requestIsEnterpriseUser: () => {},
  setPage: () => {},
};

const mapStateToProps = (store) => ({
  activeCourseRun: store.activeCourseRun,
  userLanguage: store.userLanguage,
  userLoggedIn: store.userLoggedIn,
  isEnterpriseUser: store.enterpriseUser,
});

const mapDispatchToProps = (dispatch) => ({
  onSetActiveCourseRun(courseRun) {
    dispatch(setActiveCourseRun(courseRun));
  },
  requestUserLoggedIn() {
    dispatch(getUserLoggedIn());
  },
  requestIsEnterpriseUser() {
    dispatch(enterpriseUser());
  },
  setPage(page) {
    dispatch(setCurrentPage(page));
  },
});

const OpenCourseDetailPageConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withLocation(injectIntl(OpenCourseDetailPage)));

export default track(({ pageContext, location }) => ({
  page: 'course',
  subject: pageContext.course.subjects?.[0]?.slug,
  url: location.href,
  mobile: false,
  active_course_run_uuid: pageContext.course.activeCourseRun ? pageContext.course.activeCourseRun.uuid : '',
  active_course_run_key: pageContext.course.activeCourseRun ? pageContext.course.activeCourseRun.key : '',
  course_uuid: pageContext.course.uuid,
  is_self_paced: pageContext.course.activeCourseRun
    ? pageContext.course.activeCourseRun.pacingType === 'self_paced'
    : true,
}))(OpenCourseDetailPageConnected);
